import React, { ReactNode } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Send,
  ScrollText,
  LogOut,
  Shield,
  FlaskConical,
  MonitorCog,
  Settings2,
} from "lucide-react";
import { useAuthContext } from "../../contexts/AuthContext";
import "../../assets/styles/admin-dashboard.css";

// Admin is overview-only — it never creates real campaigns. AdsPower/
// Salesforce account setup is the one operational exception: admins manage
// those profiles on behalf of users, so it's admin-only rather than
// user-facing.
const NAV_ITEMS = [
  { path: "/admin", label: "Dashboard", icon: LayoutDashboard },
  { path: "/admin/campaigns", label: "Campaigns", icon: Send },
  { path: "/admin/users", label: "Users", icon: Users },
  { path: "/admin/adspower", label: "AdsPower Profiles", icon: MonitorCog },
  { path: "/admin/logs", label: "Activity Logs", icon: ScrollText },
  {
    path: "/admin/test-send",
    label: "Test Send",
    icon: FlaskConical,
    disabled: true,
    tooltip: "Salesforce integration is paused — disabled until it's resumed",
  },
  {
    path: "/admin/salesforce-send-method",
    label: "Send Method",
    icon: Settings2,
    disabled: true,
    tooltip: "Salesforce integration is paused — disabled until it's resumed",
  },
];

/**
 * Admin shell: a distinct dark/glow sidebar so it's visually obvious you're
 * in the admin surface, not the regular user app. Kept entirely separate
 * from Sidebar/Header so nothing here can affect the regular user flow.
 */
const AdminLayout: React.FC<{ children: ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthContext();

  const handleLogout = () => {
    logout();
    navigate("/signin", { replace: true });
  };

  return (
    // Fixed-height row with each side scrolling independently — the sidebar
    // must never scroll with (and disappear behind) tall page content, so it
    // gets its own height + overflow instead of relying on sticky positioning
    // against a document that can grow taller than the viewport.
    <div
      style={{
        display: "flex",
        // 100vh on mobile is computed from the browser's largest possible
        // viewport (address bar hidden), which is taller than the actual
        // touch-interactive area whenever the address bar is showing - the
        // common case. That gap can leave bottom content (e.g. pagination)
        // technically present but outside the practically reachable scroll
        // area on a real device. 100dvh tracks the real visible viewport
        // and falls back to being identical to 100vh on desktop, where
        // there's no dynamic toolbar to create the mismatch.
        height: "100dvh",
        overflow: "hidden",
        background: "#0b1020",
      }}
    >
      <aside
        className="admin-sidebar"
        style={{
          width: "260px",
          flexShrink: 0,
          height: "100vh",
          background: "linear-gradient(180deg, #0f1630 0%, #0b1020 100%)",
          borderRight: "1px solid rgba(99,102,241,0.15)",
          display: "flex",
          flexDirection: "column",
          padding: "16px 14px",
          boxSizing: "border-box",
          overflow: "hidden",
        }}
      >
        {/* Header — pinned, never scrolls */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            padding: "0 8px",
            marginBottom: "20px",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: "34px",
              height: "34px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #6366f1, #22d3ee)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 0 18px rgba(99,102,241,0.55)",
            }}
          >
            <Shield size={18} color="#0b1020" />
          </div>

          <div>
            <div style={{ color: "#fff", fontWeight: 700, fontSize: "15px", lineHeight: 1.2 }}>
              Admin Console
            </div>
            <div style={{ color: "#64748b", fontSize: "11px" }}>
              Auto Lead Gen
            </div>
          </div>
        </div>

        {/* Nav — the ONLY scrollable region in the sidebar. minHeight:0
            overrides flexbox's default min-height:auto, which otherwise
            refuses to let a flex child shrink below its content size and
            would push the footer (Sign out) below the visible area instead
            of scrolling internally. */}
        <nav
          className="custom-scrollbar"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "2px",
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
          }}
        >
          {NAV_ITEMS.map((item) => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;

            return (
              <div
                key={item.path}
                onClick={() => !item.disabled && navigate(item.path)}
                title={item.disabled ? item.tooltip : undefined}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "9px 12px",
                  borderRadius: "10px",
                  cursor: item.disabled ? "not-allowed" : "pointer",
                  color: item.disabled ? "#475569" : isActive ? "#fff" : "#94a3b8",
                  opacity: item.disabled ? 0.5 : 1,
                  background: isActive
                    ? "linear-gradient(90deg, rgba(99,102,241,0.25), rgba(34,211,238,0.08))"
                    : "transparent",
                  border: isActive
                    ? "1px solid rgba(99,102,241,0.4)"
                    : "1px solid transparent",
                  fontSize: "14px",
                  fontWeight: isActive ? 600 : 500,
                  transition: "background 0.15s ease, color 0.15s ease",
                }}
              >
                <Icon size={17} />
                {item.label}
              </div>
            );
          })}
        </nav>

        <div
          style={{
            borderTop: "1px solid rgba(148,163,184,0.12)",
            paddingTop: "12px",
            marginTop: "12px",
            flexShrink: 0,
          }}
        >
          <div style={{ padding: "0 8px", marginBottom: "10px" }}>
            <div style={{ color: "#e2e8f0", fontSize: "13px", fontWeight: 600 }}>
              {user?.fname || "Admin"}
            </div>
            <div style={{ color: "#64748b", fontSize: "11px" }}>{user?.email}</div>
          </div>

          <button
            onClick={handleLogout}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              width: "100%",
              padding: "10px 14px",
              borderRadius: "10px",
              border: "1px solid rgba(148,163,184,0.15)",
              background: "transparent",
              color: "#94a3b8",
              fontSize: "13px",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            <LogOut size={15} />
            Sign out
          </button>
        </div>
      </aside>

      <main
        className="custom-scrollbar"
        style={{
          flex: 1,
          background: "#f3f4f9",
          height: "100dvh",
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        {children}
      </main>
    </div>
  );
};

export default AdminLayout;
